import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase/config";
import { jsPDF } from "jspdf";
import "jspdf/dist/polyfills.es.js";
import { Check } from "lucide-react";

export default function LabellisationForm() {
  const [phases, setPhases] = useState({ phase1: true, phase2: false });
  const [formData, setFormData] = useState({});
  const [signature, setSignature] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePhaseChange = (e) => {
    const { name, checked } = e.target;
    setPhases((prev) => ({ ...prev, [name]: checked }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    // 1️⃣ On reset les messages
    setSuccess("");
    setError("");

    // 2️⃣ On essaie de générer le PDF
    try {
      const doc = new jsPDF("p", "pt", "a4");
      const formElement = document.getElementById("labellisation-form");

      await doc.html(formElement, {
        callback: async (pdfDoc) => {
          const pdfBase64 = pdfDoc.output("datauristring");

          // 3️⃣ On essaie d’envoyer le PDF
          try {
            const functions = getFunctions(app);
            const sendDoc = httpsCallable(functions, "sendDocument");
            const result = await sendDoc({ pdf: pdfBase64 });

            if (result.data.success) {
              setSuccess("Le PDF a été généré et envoyé avec succès !");
              // on efface le message au bout de 5s
              setLoading(false);
              setTimeout(() => setSuccess(""), 5000);
            } else {
              // Cas où la function renvoie success: false
              setLoading(false);
              setError(result.data.error || "Échec de l’envoi du PDF.");
            }
          } catch (sendErr) {
            setLoading(false);
            console.error("Erreur lors de l’envoi du PDF :", sendErr);
            setError("Erreur réseau ou serveur durant l'envoi.");
          }
        },
        x: 20,
        y: 20,
        html2canvas: { scale: 0.5 },
      });
    } catch (pdfErr) {
      setLoading(false);
      console.error("Erreur lors de la génération du PDF :", pdfErr);
      setError("Impossible de générer le PDF.");
    }
  };

  return (
    <div className="bg-gray-50 min-h-auto py-10 px-4">
      <form
        id="labellisation-form"
        className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 space-y-8"
        onSubmit={handleSubmit}
      >
        <h1 className="text-2xl font-semibold ">
          Demande de Labellisation CEISNA
        </h1>

        {/* Phases */}
        <fieldset>
          <legend className="font-medium">
            Veuillez cocher la ou les phases :
          </legend>
          <div className="flex space-x-8 mt-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="phase1"
                checked={phases.phase1}
                onChange={handlePhaseChange}
                className="form-checkbox text-[#0c40b7] mr-2"
              />
              Phase 1 uniquement (CCNS)
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="phase2"
                checked={phases.phase2}
                onChange={handlePhaseChange}
                className="form-checkbox text-[#0c40b7] mr-2"
              />
              Phase 1 + Phase 2 (CCNS + EUST)
            </label>
          </div>
        </fieldset>

        {/* 1. Informations Générales */}
        <section>
          <h2 className="font-semibold">1. Informations Générales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {[
              "Nom de l’application",
              "Version actuelle",
              "Date de mise sur le marché ou de lancement",
              "Organisation ou porteur du projet",
              "Statut juridique",
              "Pays",
              "Lien de téléchargement ou site web",
              "Contact principal (nom, fonction, email, téléphone)",
            ].map((field) => (
              <div key={field} className="flex flex-col">
                <label className="text-sm font-medium mb-1">{field}</label>
                <input
                  name={field}
                  onChange={handleChange}
                  type="text"
                  className="border rounded px-3 py-2 focus:ring-green-300 focus:outline-none"
                />
              </div>
            ))}
          </div>
        </section>

        {/* 2. Phase 1 */}
        <section>
          <h2 className="font-semibold">2. Phase 1: CCNS</h2>
          <div className="space-y-6 mt-4">
            {[
              {
                title: "🔒 Sécurité et protection des données personnelles",
                questions: [
                  "L’application respecte-t-elle les lois locales ou le RGPD ?",
                  "Politique de confidentialité accessible ?",
                  "Mécanismes de sécurité (chiffrement, authentification) ?",
                ],
              },
              {
                title: "⚖️ Conformité réglementaire et technique",
                questions: ["Documentation technique et registre..."],
              },
              {
                title: "✅ Fiabilité, performance et architecture logicielle",
                questions: ["Protocoles de tests, gestion des bugs..."],
              },
              {
                title: "🔍 Évaluation clinique (si applicable)",
                questions: ["Validation clinique, comité scientifique..."],
              },
              {
                title: "🚼 Accessibilité et ergonomie",
                questions: ["Utilisable hors connexion, public fragile..."],
              },
            ].map((section) => (
              <div key={section.title}>
                <h3 className="font-medium mb-2">{section.title}</h3>
                {section.questions.map((q) => (
                  <textarea
                    key={q}
                    name={q}
                    placeholder={q}
                    rows={2}
                    onChange={handleChange}
                    className="w-full border rounded px-3 py-2 focus:ring-green-300 focus:outline-none mb-2"
                  />
                ))}
              </div>
            ))}
          </div>
        </section>

        {/* 3. Phase 2 */}
        {phases.phase2 && (
          <section>
            <h2 className="font-semibold">3. Phase 2: EUST</h2>
            <div className="space-y-6 mt-4">
              <textarea
                name="Contexte"
                placeholder="🌍 Contexte et utilité publique"
                rows={3}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus:ring-green-300 focus:outline-none"
              />
              <textarea
                name="Partenariats"
                placeholder="🤝 Partenariats et reconnaissance"
                rows={3}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus:ring-green-300 focus:outline-none"
              />
              <textarea
                name="Impact"
                placeholder="✨ Impact estimé (IAS)"
                rows={2}
                onChange={handleChange}
                className="w-full border rounded px-3 py-2 focus:ring-green-300 focus:outline-none"
              />
            </div>
          </section>
        )}

        {/* 4. Déclaration */}
        <section>
          <h2 className="font-semibold">4. Déclaration</h2>
          <div className="mt-4 space-y-4">
            <input
              name="Lieu"
              placeholder="Fait à"
              type="text"
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 focus:ring-green-300 focus:outline-none"
            />
            <input
              name="Date signature"
              type="date"
              onChange={handleChange}
              className="w-full border rounded px-3 py-2 focus:ring-green-300 focus:outline-none"
            />
            <input
              name="Signature"
              placeholder="Signature  (ex:Jean)"
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              className="w-full border rounded px-3 py-2 focus:ring-green-300 focus:outline-none"
            />
          </div>
        </section>

        <div className="text-center">
          <button
            type="submit"
            className="mt-6 px-6 py-3 bg-[#0c40b7] text-white rounded hover:opacity-90 transition"
          >
            {loading ? "Chargement..." : "Envoyer"}
          </button>
          {success && <p className="mt-4 text-green-700">{success}</p>}
        </div>

        {error && (
          <div className="text-center text-red-600 font-medium">{error}</div>
        )}
      </form>
      <div className="max-w-4xl flex flex-col space-y-2 mt-7 mx-auto">
        <section>
          <h2 className="font-semibold">5. Envoi du dossier</h2>
          <p className="mt-2">
            Merci de rassembler vos documents au format PDF et de nous les
            transmettre par email à l'adresse suivante{" "}
            <a
              href="mailto:certification@ceisna.org"
              className="text-[#0c40b7] underline"
            >
              certification@ceisna.org
            </a>
            .
          </p>
        </section>
        <div className=" italic">
          <h3 className=" mb-2 font-semibold ">Pièces à joindre</h3>
          <ul className=" flex flex-col space-y-2">
            {[
              "Analyse des risques",
              "Évaluation de la cybersécurité (si logiciel connecté)",
              "Évaluation clinique (soit étude propre, soit équivalence)",
              "Politique de confidentialité",
              "Documentation technique",
              "Description fonctionnelle",
              "Captures d’écran / lien démo",
              "Justificatif juridique",
            ].map((item) => (
              <li className=" flex space-x-2 items-center">
                <Check className="  text-[0.9rem] h-4 text-green-600" />{" "}
                <span>{item}</span>{" "}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
